<template>
  <div>
    <page-header :title="$t('system.text_188', [$t('dictionary.policy')])" />
    <page-body need-margin-bottom>
      <template v-if="loading">
        <div class="text-center">
          <a-icon type="loading" />
          <p>{{$t('system.text_318', [$t('dictionary.policy')])}}</p>
        </div>
      </template>
      <template v-else>
        <policy-form
          ref="policyForm"
          is-update
          :policy="policy"
          :permissions="permissions"
          :edit-type="editType"
          :policy-loading="policyLoading"
          @edit-type-change="handleEditTypeChange" />
      </template>
    </page-body>
    <page-footer>
      <div slot="right">
        <a-button v-if="!loading" class="mr-3" type="primary" @click="doUpdata" :loading="submiting">{{ submitButtonText }}</a-button>
        <a-button @click="handleCancel">{{ $t('common.cancel') }}</a-button>
      </div>
    </page-footer>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import workflowMixin from '@/mixins/workflow'
import { WORKFLOW_TYPES } from '@/constants/workflow'
import { uuid } from '@/utils/utils'
import { DEFAULT_ACTIONS_KEY } from './constants'
import { genPolicyGroups } from './utils'
import PolicyForm from './components/Form'

export default {
  name: 'PolicyUpdate',
  components: {
    PolicyForm,
  },
  mixins: [workflowMixin],
  data () {
    return {
      loading: false,
      policy: {},
      permissions: {},
      submiting: false,
      POLICY_GROUPS: genPolicyGroups(),
      editType: 'checkbox',
      policyLoading: false,
    }
  },
  computed: {
    ...mapGetters(['userInfo']),
    isExecuteResourceOrderSetEnabled () {
      return this.checkWorkflowEnabled(WORKFLOW_TYPES.EXECUTE_POLICY_UPDATE)
    },
    submitButtonText () {
      return this.isExecuteResourceOrderSetEnabled ? this.$t('system.text_532') : this.$t('common.ok')
    },
  },
  watch: {
    editType () {
      this.$nextTick(() => {
        this.getPolicy().then(policyRes => {
          this.policy = policyRes.data
        })
      })
    },
  },
  created () {
    this.getAll()
  },
  methods: {
    getAll () {
      this.loading = true
      this.getPolicy().then((policyRes) => {
        this.policy = policyRes.data
        this.getPermissions().then(permissionsRes => {
          this.permissions = permissionsRes.data
          this.loading = false
        }).catch(() => {
          this.loading = false
        })
      }).catch(() => {
        this.loading = false
      })
    },
    async getPolicy () {
      const params = {}
      if (this.editType === 'yaml') {
        params.format = 'yaml'
      }
      this.policyLoading = true
      try {
        const response = await this.$http.get(`/v1/policies/${this.$route.query.id}`, {
          params,
        })
        this.policyLoading = false
        return response
      } catch (error) {
        throw error
      }
    },
    getPermissions () {
      const bodyData = this.genPermissionsBodyData()
      return this.$http.post(`/v1/auth/permissions?policy=${this.$route.query.id}`, bodyData)
    },
    genPermissionsBodyData () {
      const ret = {}
      const scope = this.policy.scope
      for (let i = 0, len = this.POLICY_GROUPS.length; i < len; i++) {
        const item = this.POLICY_GROUPS[i]
        for (let j = 0, jlen = item.resources.length; j < jlen; j++) {
          const resource = item.resources[j]
          for (let k = 0, klen = DEFAULT_ACTIONS_KEY.length; k < klen; k++) {
            const action = DEFAULT_ACTIONS_KEY[k]
            if (resource.resource === '*') {
              ret[`${item.service}_${resource.resource}_${action}`] = [scope, item.service, resource.resource, action]
            } else {
              ret[`${resource.resource}_${action}`] = [scope, item.service, resource.resource, action]
            }
          }
          if (resource.extras && resource.extras) {
            resource.extras.forEach(extras => {
              ret[`${resource.resource}_${extras.action}_${extras.value}`] = [scope, item.service, resource.resource, extras.action, extras.value]
            })
          }
        }
      }
      return ret
    },
    async doUpdata () {
      this.submiting = true
      try {
        const data = await this.$refs.policyForm.getData()
        data.tag_update_policy = 'replace'
        data.tags_action = 'replace'
        // 如果启用了 execute-policy-update 工单类型，则创建工单
        if (this.isExecuteResourceOrderSetEnabled) {
          await this.updatePolicyByWorkflow(data)
        } else {
          // 否则直接更新
          await this.$http.patch(`/v1/auth/policies/${this.policy.id}`, data)
          this.$store.commit('keepAlive/ADD_DELAY_EVENT', { name: 'ResourceListSingleRefresh', params: this.policy.id })
          this.$router.push('/policy')
        }
      } catch (error) {
        throw error
      } finally {
        this.submiting = false
      }
    },
    async updatePolicyByWorkflow (data) {
      try {
        // 获取项目和项目域信息
        const projectId = this.policy.project_id || this.userInfo.projectId || ''
        const domainId = this.policy.domain_id || this.userInfo.projectDomainId || 'default'
        const projectDomainName = this.policy.project_domain || (domainId === 'default' ? 'Default' : domainId)

        // 创建 resource_order_sets
        const orderSetManager = new this.$Manager('resource_order_sets')
        const { displayname, name } = this.userInfo
        const orderSetData = {
          auto_execute: false,
          name: this.$t('iam.policy_update_workflow_name', [displayname || name, this.$moment().format('YYYY-MM-DD'), uuid()]),
          parameters: [{
            action: 'update',
            resource: 'policies',
            resource_id: this.policy.id,
            project: projectId || '',
            project_domain: projectDomainName,
            parameter: data,
          }],
        }
        const orderSetRes = await orderSetManager.create({ data: orderSetData })

        // 创建工单
        const variables = {
          process_definition_key: WORKFLOW_TYPES.EXECUTE_POLICY_UPDATE,
          initiator: this.userInfo.id,
          ids: orderSetRes.data.id,
          parameter: '{}',
          project: projectId || '',
          project_domain: domainId,
          execute_resource_order_set_type: 'policies_update',
        }
        await this.createWorkflow(variables)
        this.$message.success(this.$t('common.worflow_tip', [this.$t('system.text_188', [this.$t('dictionary.policy')])]))
        this.$router.push('/workflow')
      } catch (error) {
        throw error
      }
    },
    handleCancel () {
      this.$router.push('/policy')
    },
    handleEditTypeChange (type) {
      this.editType = type
    },
  },
}
</script>
